import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container } from "../components/styled.ts";
import { Typography } from "../components/typography/index.tsx";
import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { WrapDigest } from "../components/wrapDigest/index.tsx";
import { WrapFeedback } from "../components/wrapFeedback/index.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { useMobile } from "../hooks/isMobile.tsx";

interface Wrap {
  wrapDate: string;
  slug: string;
  title: string;
  headline?: string;
  markdownBody?: string;
  storyCount?: number;
  createdAt?: string;
}

const SLUG_PREFIX = "finq-daily-wrap-";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SLUG_PATTERN = /^finq-daily-wrap-\d{4}-\d{2}-\d{2}$/;

const dateFromSlug = (slug: string | undefined): string | null => {
  if (!slug || !SLUG_PATTERN.test(slug)) {
    return null;
  }
  const date = slug.slice(SLUG_PREFIX.length);
  return DATE_PATTERN.test(date) ? date : null;
};

const formatDate = (dateKey: string) => {
  if (!DATE_PATTERN.test(dateKey)) return dateKey;
  const [y, m, d] = dateKey.split("-").map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** Strip newsletter chrome the model sometimes appends — CTA lives in the page, not the body. */
const cleanWrapBody = (body: string): string =>
  body
    .replace(/\n---\s*\n[\s\S]*$/m, "\n")
    .replace(/^---\s*\nGet FinQ Alerts[\s\S]*$/gim, "")
    .replace(/\nGet FinQ Alerts on WhatsApp[^\n]*/gi, "")
    .trim();

const NewsArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isMobile } = useMobile();
  const [wrap, setWrap] = useState<Wrap | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "notfound">("loading");

  useEffect(() => {
    let cancelled = false;
    const date = dateFromSlug(slug);
    if (!date) {
      setStatus("notfound");
      return;
    }

    fetch(API_URLS.WRAP_BY_DATE(date))
      .then((res) => {
        if (!res.ok) throw new Error("not found");
        return res.json();
      })
      .then((data: Wrap) => {
        if (cancelled) return;
        if (!data || typeof data.title !== "string" || typeof data.markdownBody !== "string") {
          setStatus("notfound");
          return;
        }
        setWrap(data);
        setStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setStatus("notfound");
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <>
      <Seo
        title={wrap ? `${wrap.title} | FinQ Daily Wrap` : "FinQ Daily Wrap | Corporate India daily digest"}
        description={
          wrap
            ? (wrap.headline || wrap.title).replace(/\*\*/g, "").slice(0, 155)
            : "End-of-day market pulse and the corporate filings that mattered for NSE & BSE, summarised by AI."
        }
        path={`/news/${encodeURIComponent(slug)}`}
        noindex={status === "notfound"}
      />
      <Header active="news" />
      <div
        style={{
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 120px)",
          minHeight: "100vh",
        }}
      >
        <Container
          width={isMobile ? "min(calc(100% - 28px), 640px)" : "min(calc(100% - 40px), 640px)"}
          justifyContent="center"
          margin="0 auto"
          padding={isMobile ? "12px 0 64px" : "20px 0 72px"}
          column
          gap={"0"}
          style={{ boxSizing: "border-box" }}
        >
          <Link
            to="/news"
            style={{
              display: "block",
              marginBottom: 16,
              cursor: "pointer",
              width: "fit-content",
              fontFamily: "Neue Montreal, system-ui, sans-serif",
              fontSize: 14,
              color: "#2563eb",
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            ← All wraps
          </Link>

          {status === "loading" && (
            <Typography tag="p" fontFamily="Neue Montreal" fontSize={"16px"} color="#64748b">
              Loading…
            </Typography>
          )}

          {status === "notfound" && (
            <Typography tag="p" fontFamily="Neue Montreal" fontSize={"18px"} color="#0f172a">
              This wrap could not be found.
            </Typography>
          )}

          {status === "loaded" && wrap && (
            <>
              {/* Masthead only — no meta counts, no duplicate headline teaser */}
              <h1
                style={{
                  fontFamily: "Neue Montreal, system-ui, sans-serif",
                  fontSize: isMobile ? 28 : 36,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: "#0b1220",
                  margin: "0 0 8px 0",
                  textAlign: "center",
                }}
              >
                FinQ Daily Wrap
              </h1>
              <p
                style={{
                  fontFamily: "Neue Montreal, system-ui, sans-serif",
                  fontSize: isMobile ? 14 : 15,
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                  color: "#64748b",
                  margin: 0,
                  textAlign: "center",
                }}
              >
                {formatDate(wrap.wrapDate)}
              </p>
              <div
                style={{
                  height: 1,
                  background: "#e2e8f0",
                  margin: "14px 0 4px",
                  width: "100%",
                }}
              />

              {wrap.markdownBody && <WrapDigest content={cleanWrapBody(wrap.markdownBody)} />}

              <WrapFeedback wrapDate={wrap.wrapDate} />

              <div
                style={{
                  marginTop: 32,
                  padding: isMobile ? "20px 18px" : "24px 24px",
                  borderRadius: 14,
                  background: "#0f172a",
                  boxSizing: "border-box",
                  width: "100%",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: "Neue Montreal, system-ui, sans-serif",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#fff",
                    marginBottom: 6,
                  }}
                >
                  Alerts for stocks you hold
                </div>
                <div
                  style={{
                    fontFamily: "Neue Montreal, system-ui, sans-serif",
                    fontSize: 14,
                    lineHeight: "21px",
                    color: "rgba(255,255,255,0.75)",
                    marginBottom: 16,
                    maxWidth: 420,
                  }}
                >
                  FinQ Alerts on WhatsApp — material filings in real time.
                </div>
                <Link
                  to="/subscription-plans"
                  style={{
                    display: "inline-block",
                    background: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "10px 20px",
                    fontFamily: "Neue Montreal, system-ui, sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0f172a",
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                >
                  Get FinQ Alerts
                </Link>
              </div>

              <p
                style={{
                  fontFamily: "Neue Montreal, system-ui, sans-serif",
                  fontSize: 11,
                  lineHeight: "17px",
                  color: "#94a3b8",
                  margin: "18px 0 0",
                }}
              >
                Disclaimer: AI generated, subject to errors, DYOD. Not an investment advice.
              </p>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default NewsArticle;
